import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { User } from "app/services/models/user.model";
import { CurrentService } from "app/services/current.service";
import { RoleService } from "app/services/role.service";
import { CoreMenuItem } from "@core/types";

@Injectable({
  providedIn: "root",
})
export class CoreMenuService {
  currentUser: User;
  onItemCollapsed: Subject<any>;
  onItemCollapseToggled: Subject<any>;

  private _onMenuRegistered: BehaviorSubject<any>;
  private _onMenuUnregistered: BehaviorSubject<any>;
  private _onMenuChanged: BehaviorSubject<any>;
  private _currentMenuKey: string;
  private _registry: { [key: string]: any } = {};

  constructor(private currentService: CurrentService, private roleService: RoleService) {
    this.currentService.currentUser.subscribe((x) => (this.currentUser = x));

    // S'abonner aux changements de permissions pour mettre à jour le menu
    this.roleService.permissions$.subscribe(() => {
      if (this._currentMenuKey) {
        this._onMenuChanged.next(this._currentMenuKey);
      }
    });

    this.onItemCollapsed = new Subject();
    this.onItemCollapseToggled = new Subject();

    this._currentMenuKey = null;
    this._onMenuRegistered = new BehaviorSubject(null);
    this._onMenuUnregistered = new BehaviorSubject(null);
    this._onMenuChanged = new BehaviorSubject(null);
  }

  /**
   * Vérifie si un élément de menu doit être visible
   * Supporte à la fois l'ancien système (role) ET le nouveau (permission)
   *
   * Logique PRIORITÉ AU NOUVEAU SYSTÈME:
   * - Si permission est défini -> utiliser UNIQUEMENT le système de permissions
   * - Sinon, si role est défini -> utiliser l'ancien système (rétrocompatibilité)
   *
   * Ceci permet une transition progressive : une fois qu'un item a une permission,
   * l'ancien système de rôles est ignoré pour cet item.
   */
  isMenuItemVisible(item: CoreMenuItem): boolean {
    // Si pas d'utilisateur connecté, ne rien afficher
    if (!this.currentUser) {
      return false;
    }

    // VÉRIFICATION GLOBAL ADMIN ONLY
    // Si l'item est réservé aux Global Admins, vérifier le flag isGlobalAdmin
    if (item.globalAdminOnly) {
      return this.currentUser.isGlobalAdmin === true;
    }

    // NOUVEAU SYSTÈME DE PERMISSIONS (prioritaire)
    // Si une permission est définie, on utilise UNIQUEMENT ce système
    if (item.permission) {
      const hasPermission = this.roleService.hasPermission(item.permission);
      // Debug logging
      if (!hasPermission) {
        console.log(`🔒 Menu "${item.title}" caché - permission "${item.permission}" manquante`);
        console.log(`📋 Permissions actuelles:`, this.roleService.getCurrentPermissions());
      }
      return hasPermission;
    }

    if (item.permissions && item.permissions.length > 0) {
      const checkMode = item.permissionCheckMode || "any";
      if (checkMode === "all") {
        return this.roleService.hasAllPermissions(item.permissions);
      } else {
        return this.roleService.hasAnyPermission(item.permissions);
      }
    }

    // ANCIEN SYSTÈME DE RÔLES (rétrocompatibilité)
    // Utilisé uniquement si aucune permission n'est définie
    if (item.role && item.role.length > 0) {
      return item.role.includes(this.currentUser.role);
    }

    // Si aucune restriction, l'élément est visible
    return true;
  }

  get onMenuRegistered(): Observable<any> {
    return this._onMenuRegistered.asObservable();
  }

  get onMenuUnregistered(): Observable<any> {
    return this._onMenuUnregistered.asObservable();
  }

  get onMenuChanged(): Observable<any> {
    return this._onMenuChanged.asObservable();
  }

  register(key, menu): void {
    if (this._registry[key]) {
      console.error(
        `Menu with the key '${key}' already exists. Either unregister it first or use a unique key.`
      );

      return;
    }

    this._registry[key] = menu;
    this._onMenuRegistered.next([key, menu]);
  }

  unregister(key): void {
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
    }

    delete this._registry[key];

    this._onMenuUnregistered.next(key);
  }

  getMenu(key): any {
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    return this._registry[key];
  }

  getCurrentMenu(): any {
    if (!this._currentMenuKey) {
      console.warn(`The current menu is not set.`);

      return;
    }

    return this.getMenu(this._currentMenuKey);
  }

  setCurrentMenu(key): void {
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    this._currentMenuKey = key;

    this._onMenuChanged.next(key);
  }
}
